import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  BookOpen,
  Copy,
  Edit,
  Eye,
  Filter,
  Mail,
  Plus,
  Save,
  Search,
  Trash2,
  Users,
  X
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useGrandeEcole from '../../stores/useGrandeEcole';


export function CohortesPilotage({ darkMode = true, isEditable = false }) {
  const {
    cohortes = [],
    courses = [],
    staff = [],
    learners = [],
    loading,
    createCohorte,
    updateCohorte,
    deleteCohorte,
    openCompose
  } = useGrandeEcole();

  const [search, setSearch] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    responsable: 'all',
    nombreApprenants: { min: 0, max: Infinity }
  });

  const [editedCohortes, setEditedCohortes] = useState({});
  const [showNewForm, setShowNewForm] = useState(false);
  const [newCohorte, setNewCohorte] = useState({
    nom: '',
    slug: '',
    description: '',
    debut: '',
    finPrevue: '',
    finReelle: '',
    status: 'planned',
    parcoursIds: [],
    responsableId: '',
    notesInternes: ''
  });

  const [selectedCohortes, setSelectedCohortes] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [detailModal, setDetailModal] = useState(null);

  const availableResponsables = staff.filter(s =>
    ['staff', 'admin', 'formateur', 'instructeur', 'Directeur Pédagogique', 'Facilitateur Senior', 'entreprise', 'pro', 'collectivité'].includes(
      s.role || s.pesaRole
    )
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy', { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const getCohorteStats = (cohorteId) => {
    const cid = String(cohorteId);
    const cohorteLearners = learners.filter(l => {
      const lid = String(l.cohorte?._id || l.cohorteId || '');
      return lid && lid === cid;
    });

    const nbEleves = cohorteLearners.length;
    if (nbEleves === 0) return { nbEleves: 0, progressionMoyenne: 0, alertesActives: 0 };

    const totalProgress = cohorteLearners.reduce((sum, l) =>
      sum + (l.stats?.globalProgress || l.globalProgress || 0), 0
    );
    const progressionMoyenne = Math.round(totalProgress / nbEleves);
    const alertesActives = cohorteLearners.filter(l =>
      (l.stats?.globalProgress || l.globalProgress || 0) < 30
    ).length;

    return { nbEleves, progressionMoyenne, alertesActives };
  };

  const filteredCohortes = useMemo(() => {
    let result = [...cohortes];

    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(c =>
        c.nom?.toLowerCase().includes(searchLower) ||
        c.slug?.toLowerCase().includes(searchLower) ||
        c.description?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status !== 'all') {
      result = result.filter(c => c.status === filters.status);
    }

    if (filters.responsable !== 'all') {
      result = result.filter(c =>
        String(c.responsableId?._id || c.responsableId) === String(filters.responsable)
      );
    }

    result = result.filter(c => {
      const stats = c.stats || getCohorteStats(c._id || c.id);
      const nb = stats.nbEleves ?? c.eleves ?? 0;
      return nb >= filters.nombreApprenants.min && nb <= filters.nombreApprenants.max;
    });

    return result;
  }, [cohortes, learners, search, filters]);

  const startEditCohorte = (cohorte) => {
    const id = cohorte._id || cohorte.id;
    if (!id) return;

    setEditedCohortes(prev => ({
      ...prev,
      [id]: {
        ...cohorte,
        nom: cohorte.nom || '',
        slug: cohorte.slug || '',
        description: cohorte.description || '',
        debut: cohorte.debut ? new Date(cohorte.debut).toISOString().split('T')[0] : '',
        finPrevue: cohorte.finPrevue ? new Date(cohorte.finPrevue).toISOString().split('T')[0] : '',
        finReelle: cohorte.finReelle ? new Date(cohorte.finReelle).toISOString().split('T')[0] : '',
        status: cohorte.status || 'planned',
        parcoursIds: Array.isArray(cohorte.parcoursIds) 
          ? cohorte.parcoursIds.map(p => p._id || p)
          : [],
        responsableId: cohorte.responsableId?._id || cohorte.responsableId || '',
        notesInternes: cohorte.notesInternes || ''
      }
    }));
  };

  const updateEditedField = (id, field, value) => {
    setEditedCohortes(prev => {
      const current = prev[id];
      
      if (field === 'nom' && value) {
        const slug = value
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9\s-]/g, "")
          .trim()
          .replace(/\s+/g, "-");
        
        return {
          ...prev,
          [id]: { 
            ...current, 
            nom: value,
            slug: slug 
          }
        };
      }

      let validated = value;
      if (field === 'finPrevue' || field === 'finReelle') {
        if (value && current.debut && new Date(value) <= new Date(current.debut)) {
          validated = { value, invalid: true };
        }
      }

      return {
        ...prev,
        [id]: { ...current, [field]: validated }
      };
    });
  };

  const saveCohorte = async (id) => {
    const edited = editedCohortes[id];
    if (!edited) return;

    const errors = [];
    if (!edited.nom) errors.push('Nom obligatoire');
    if (!edited.slug) errors.push('Slug obligatoire');
    if (!edited.debut) errors.push('Date début obligatoire');
    if (!edited.responsableId) errors.push('Responsable obligatoire');
    if (edited.finPrevue?.invalid) errors.push('Date fin prévue invalide');
    if (edited.finReelle?.invalid) errors.push('Date fin réelle invalide');

    if (errors.length > 0) {
      toast.error(`Erreurs :\n- ${errors.join('\n- ')}`);
      return;
    }

    const responsable = staff.find(s => (s._id || s.id) === edited.responsableId);
    
    const confirm = window.confirm(
      `Sauvegarder ?\n\n` +
      `Nom : ${edited.nom}\n` +
      `Responsable : ${responsable ? `${responsable.nom} ${responsable.prenom}` : 'N/A'}\n` +
      `Parcours : ${edited.parcoursIds.length}`
    );

    if (!confirm) return;

    try {
      await updateCohorte(id, {
        nom: edited.nom,
        slug: edited.slug,
        description: edited.description,
        debut: edited.debut,
        finPrevue: edited.finPrevue || null,
        finReelle: edited.finReelle || null,
        status: edited.status,
        parcoursIds: edited.parcoursIds,
        responsableId: edited.responsableId,
        notesInternes: edited.notesInternes
      });

      setEditedCohortes(prev => {
        const { [id]: _, ...rest } = prev;
        return rest;
      });

      toast.success('Cohorte mise à jour');
    } catch (err) {
      toast.error(`❌ ${err.message}`);
    }
  };

  const cancelEditCohorte = (id) => {
    setEditedCohortes(prev => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleNewChange = (field, value) => {
    setNewCohorte(prev => ({ ...prev, [field]: value }));

    if (field === 'nom' && value) {
      const slug = value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");
      setNewCohorte(prev => ({ ...prev, slug }));
    }
  };

  const saveNewCohorte = async () => {
    if (!newCohorte.nom || !newCohorte.slug || !newCohorte.debut || !newCohorte.responsableId) {
      toast.error('Nom, slug, date début et responsable obligatoires');
      return;
    }

    try {
      const result = await createCohorte(newCohorte);
      
      if (result.success) {
        setNewCohorte({
          nom: '', slug: '', description: '', debut: '', finPrevue: '', finReelle: '',
          status: 'planned', parcoursIds: [], responsableId: '', notesInternes: ''
        });
        setShowNewForm(false);
        toast.success(`Cohorte "${result.cohorte.nom}" créée`);
      }
    } catch (err) {
      toast.error(`❌ ${err.message}`);
    }
  };

  const toggleSelectCohorte = (id) => {
    setSelectedCohortes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllCohortes = () => {
    setSelectedCohortes(new Set(filteredCohortes.map(c => c._id || c.id)));
  };

  const clearSelection = () => setSelectedCohortes(new Set());

  const handleBulkAction = async () => {
    if (selectedCohortes.size === 0 || !bulkAction) {
      toast.error('Sélectionnez des cohortes et une action');
      return;
    }

    const ids = Array.from(selectedCohortes);

    switch (bulkAction) {
      case 'delete-selected':
        if (!window.confirm(`⚠️ Supprimer ${ids.length} cohorte(s) ?`)) return;
        const code = prompt('Tapez "SUPPRIMER" :');
        if (code !== 'SUPPRIMER') return;

        try {
          for (const id of ids) await deleteCohorte(id);
          toast.success(`${ids.length} cohortes supprimées`);
          clearSelection();
        } catch (err) {
          toast.error(`❌ ${err.message}`);
        }
        break;

      default:
        toast.info('Action non implémentée');
    }

    setBulkAction('');
  };

  const handleDuplicate = async (cohorteId) => {
    const original = cohortes.find(c => (c._id || c.id) === cohorteId);
    if (!original || !window.confirm(`Dupliquer "${original.nom}" ?`)) return;

    const duplicate = {
      nom: `${original.nom} (Copie)`,
      slug: `${original.slug || ''}-copy-${Date.now()}`,
      description: original.description,
      debut: new Date().toISOString().split('T')[0],
      finPrevue: original.finPrevue,
      responsableId: original.responsableId?._id || original.responsableId,
      parcoursIds: Array.isArray(original.parcoursIds) 
        ? original.parcoursIds.map(p => p._id || p)
        : [],
      status: 'planned',
      notesInternes: original.notesInternes
    };

    try {
      await createCohorte(duplicate);
      toast.success('Cohorte dupliquée');
    } catch (err) {
      toast.error(`❌ ${err.message}`);
    }
  };

  const cohortesActives = cohortes.filter(c => c.status === 'active');
  const totalEleves = cohortes.reduce((sum, c) => {
    const stats = c.stats || getCohorteStats(c._id || c.id);
    return sum + (stats.nbEleves || 0);
  }, 0);

  if (loading) {
    return (
      <div className="space-y-8 opacity-50">
        <div className="h-10 bg-zinc-800 animate-pulse rounded" />
        <div className="h-64 bg-zinc-800 animate-pulse rounded" />
      </div>
    );
  }

  return (
  <div className="space-y-8 relative">

    {/* HEADER */}
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-zinc-800 pb-4 gap-4">
        <div>
          <h3 className="text-xl md:text-2xl font-black tracking-tighter text-emerald-400 uppercase">
            Pilotage
          </h3>
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
            {filteredCohortes.length} / {cohortes.length} affichées – {cohortesActives.length} actives – {totalEleves} apprenants
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:flex-none">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher nom, slug..."
              className="w-full md:w-64 pl-10 pr-4 py-2 text-sm font-mono bg-zinc-900/50 border border-zinc-800 text-zinc-200 placeholder-zinc-600 outline-none focus:border-emerald-500/50 rounded-none"
            />
          </div>
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`px-4 py-2 text-xs font-bold uppercase border transition-all ${
              showAdvancedFilters
                ? 'bg-emerald-600/30 border-emerald-500/50 text-emerald-300'
                : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <Filter size={14} className="inline mr-2" />
            Filtres
          </button>
          <button
            onClick={() => setShowNewForm(!showNewForm)}
            className="px-4 md:px-6 py-2 md:py-2.5 bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-500/30 text-emerald-300 font-medium text-sm transition-colors flex items-center gap-2"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">Nouvelle promotion</span>
            <span className="sm:hidden">Nouvelle</span>
          </button>
        </div>
      </div>

      {/* FILTRES AVANCÉS */}
      {showAdvancedFilters && (
        <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded space-y-4 animate-in fade-in slide-in-from-top-2">
          <h4 className="text-sm font-bold text-zinc-300 uppercase tracking-wide">Filtres Avancés</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] uppercase text-zinc-500 mb-2">Status</label>
              <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-emerald-500">
                <option value="all">Tous</option>
                <option value="planned">Planifiée</option>
                <option value="active">Active</option>
                <option value="finished">Terminée</option>
                <option value="archived">Archivée</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase text-zinc-500 mb-2">Responsable</label>
              <select value={filters.responsable} onChange={e => setFilters({ ...filters, responsable: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-emerald-500">
                <option value="all">Tous</option>
                {availableResponsables.map(r => (
                  <option key={r._id || r.id} value={r._id || r.id}>{r.nom} {r.prenom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase text-zinc-500 mb-2">Apprenants min</label>
              <input type="number" min="0" value={filters.nombreApprenants.min}
                onChange={e => setFilters({ ...filters, nombreApprenants: { ...filters.nombreApprenants, min: Number(e.target.value) } })}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-emerald-500" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setFilters({ status: 'all', responsable: 'all', nombreApprenants: { min: 0, max: Infinity } })}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-xs">
              Réinitialiser
            </button>
            <button onClick={() => setShowAdvancedFilters(false)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
              Appliquer
            </button>
          </div>
        </div>
      )}

      {/* BARRE BULK ACTIONS */}
      {selectedCohortes.size > 0 && (
        <div className="bg-emerald-950/20 border border-emerald-800/50 p-3 md:p-4 rounded flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-emerald-300 font-bold text-sm">
              {selectedCohortes.size} sélectionné(s)
            </span>
            <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 px-3 py-1.5 text-sm outline-none focus:border-emerald-500">
              <option value="">Action...</option>
              <option value="archive-all">Archiver</option>
              <option value="delete-selected">❌ Supprimer</option>
            </select>
            <button onClick={handleBulkAction} disabled={!bulkAction}
              className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-xs font-medium">
              Exécuter
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={selectAllCohortes}
              className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-xs">
              Tout sélectionner
            </button>
            <button onClick={clearSelection}
              className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-xs">
              Désélectionner
            </button>
          </div>
        </div>
      )}
    </div>

    {/* FORMULAIRE NOUVELLE COHORTE */}
    {showNewForm && (
      <div className="border border-emerald-800/50 bg-emerald-950/20 p-4 md:p-6 rounded space-y-6">
        <h4 className="text-lg font-black text-emerald-300">Nouvelle promotion</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] uppercase text-zinc-400 mb-1">Nom *</label>
            <input type="text" value={newCohorte.nom} onChange={e => handleNewChange('nom', e.target.value)}
              placeholder="Promo 2026"
              className="w-full bg-zinc-900 border border-zinc-700 p-2.5 text-sm outline-none focus:border-emerald-500" />
            {newCohorte.slug && (
              <div className="text-[10px] text-zinc-500 mt-1 font-mono">Slug : {newCohorte.slug}</div>
            )}
          </div>
          <div>
            <label className="block text-[11px] uppercase text-zinc-400 mb-1">Responsable *</label>
            <select value={newCohorte.responsableId} onChange={e => handleNewChange('responsableId', e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 p-2.5 text-sm outline-none focus:border-emerald-500">
              <option value="">Sélectionner...</option>
              {availableResponsables.map(r => (
                <option key={r._id || r.id} value={r._id || r.id}>{r.nom} {r.prenom}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase text-zinc-400 mb-1">Date début *</label>
            <input type="date" value={newCohorte.debut} onChange={e => handleNewChange('debut', e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 p-2.5 text-sm outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="block text-[11px] uppercase text-zinc-400 mb-1">Date fin prévue</label>
            <input type="date" value={newCohorte.finPrevue} onChange={e => handleNewChange('finPrevue', e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 p-2.5 text-sm outline-none focus:border-emerald-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[11px] uppercase text-zinc-400 mb-1">Description</label>
            <textarea value={newCohorte.description} onChange={e => handleNewChange('description', e.target.value)}
              rows={3} className="w-full bg-zinc-900 border border-zinc-700 p-2.5 text-sm outline-none focus:border-emerald-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[11px] uppercase text-zinc-400 mb-1">Parcours (MasterParcours)</label>
            <select multiple value={newCohorte.parcoursIds}
              onChange={e => handleNewChange('parcoursIds', Array.from(e.target.selectedOptions, o => o.value))}
              className="w-full bg-zinc-900 border border-zinc-700 p-2.5 text-sm outline-none focus:border-emerald-500 h-32">
              {courses.map(c => (
                <option key={c._id || c.id} value={c._id || c.id}>{c.nom} ({c.slug})</option>
              ))}
            </select>
            <div className="text-[10px] text-zinc-500 mt-1">Maintenez Ctrl/Cmd pour sélectionner plusieurs parcours</div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => setShowNewForm(false)}
            className="px-5 py-2 bg-zinc-800 border border-zinc-600 text-zinc-300 text-sm">
            Annuler
          </button>
          <button onClick={saveNewCohorte}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm flex items-center gap-2">
            <Plus size={14} />
            Créer la promotion
          </button>
        </div>
      </div>
    )}

    {/* TABLEAU */}
    <div className="border border-zinc-800 overflow-hidden bg-black/40">

      {/* Header — desktop uniquement */}
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-[10px] font-black uppercase tracking-widest border-b bg-zinc-900/60 border-zinc-700 text-zinc-400">
        <div className="col-span-1 flex items-center justify-center">
          <input type="checkbox"
            checked={selectedCohortes.size === filteredCohortes.length && filteredCohortes.length > 0}
            onChange={e => e.target.checked ? selectAllCohortes() : clearSelection()}
            className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-emerald-500" />
        </div>
        <div className="col-span-3">COHORTE</div>
        <div className="col-span-2">PÉRIODE</div>
        <div className="col-span-2">RESPONSABLE</div>
        <div className="col-span-1 text-center">APPRENANTS</div>
        <div className="col-span-1 text-center">PROGRESSION</div>
        <div className="col-span-2 text-right">ACTIONS</div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-zinc-800/40 max-h-[600px] overflow-y-auto">
        {filteredCohortes.length > 0 ? (
          filteredCohortes.map(c => {
            const id = c._id || c.id;
            const isEdited = !!editedCohortes[id];
            const isSelected = selectedCohortes.has(id);
            const current = isEdited ? editedCohortes[id] : c;
            const responsable = staff.find(s => (s._id || s.id) === (current.responsableId?._id || current.responsableId));
            const stats = current.stats || getCohorteStats(id);

            return (
              <div key={id} className={`transition-colors ${
                isSelected ? 'bg-emerald-950/20 border-l-2 border-emerald-500' : 'hover:bg-emerald-950/10'
              }`}>

                {/* ═══════════════════════════════════ */}
                {/* VUE MOBILE : carte compacte         */}
                {/* ═══════════════════════════════════ */}
                <div className="md:hidden px-4 py-3 space-y-2.5">

                  {/* Ligne 1 : checkbox + nom + badge apprenants */}
                  <div className="flex items-center gap-2.5">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelectCohorte(id)}
                      className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-emerald-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      {isEdited ? (
                        <input type="text" value={current.nom || ''} onChange={e => updateEditedField(id, 'nom', e.target.value)}
                          placeholder="Nom" className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-emerald-500 rounded" />
                      ) : (
                        <>
                          <div className="font-bold text-sm truncate">{current.nom}</div>
                          <div className="text-[10px] text-zinc-500 font-mono truncate">{current.slug}</div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-950/30 text-emerald-400 font-bold text-xs rounded">
                        <Users size={10} />
                        {stats.nbEleves}
                      </div>
                    </div>
                  </div>

                  {/* Ligne 2 : responsable + période + progression */}
                  <div className="flex items-center gap-3 pl-[26px]">
                    <span className="text-[11px] text-zinc-400 truncate flex-1">
                      {isEdited ? (
                        <select value={current.responsableId || ''} onChange={e => updateEditedField(id, 'responsableId', e.target.value)}
                          className="bg-zinc-900 border border-zinc-700 px-1.5 py-1 text-xs outline-none focus:border-emerald-500 rounded w-full">
                          <option value="">Responsable...</option>
                          {availableResponsables.map(r => (
                            <option key={r._id || r.id} value={r._id || r.id}>{r.nom} {r.prenom}</option>
                          ))}
                        </select>
                      ) : (
                        responsable ? `${responsable.nom} ${responsable.prenom}` : '—'
                      )}
                    </span>
                    <span className="text-[10px] text-zinc-600 font-mono shrink-0">
                      {formatDate(current.debut)}
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <div className="w-12 bg-zinc-800 h-1 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 transition-all"
                          style={{ width: `${stats.progressionMoyenne || 0}%` }} />
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500 w-7">{stats.progressionMoyenne || 0}%</span>
                    </div>
                  </div>

                  {/* Ligne 3 : actions */}
                  <div className="flex items-center justify-end gap-1 pl-[26px]">
                    {isEdited ? (
                      <>
                        <button onClick={() => saveCohorte(id)} className="p-2 hover:text-emerald-400 transition-colors" title="Sauvegarder"><Save size={14} /></button>
                        <button onClick={() => cancelEditCohorte(id)} className="p-2 hover:text-red-400 transition-colors" title="Annuler"><X size={14} /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setDetailModal(c)} className="p-2 hover:text-sky-400 transition-colors" title="Détails"><Eye size={14} /></button>
                        <button onClick={() => startEditCohorte(c)} disabled={!isEditable}
                          className={`p-2 transition-colors ${isEditable ? 'hover:text-emerald-400' : 'opacity-40 cursor-not-allowed'}`}
                          title={isEditable ? "Éditer" : "Activez Mode Lab"}><Edit size={14} /></button>
                        {responsable && (
                          <button onClick={() => openCompose({ id: responsable._id || responsable.id, name: `${responsable.nom} ${responsable.prenom}`, email: responsable.email })}
                            className="p-2 hover:text-sky-400 transition-colors" title="Contacter"><Mail size={14} /></button>
                        )}
                        <button onClick={() => handleDuplicate(id)} className="p-2 hover:text-purple-400 transition-colors" title="Dupliquer"><Copy size={14} /></button>
                        <button onClick={() => { if (window.confirm('Supprimer cette cohorte ?')) deleteCohorte(id); }}
                          disabled={!isEditable}
                          className={`p-2 transition-colors ${isEditable ? 'hover:text-red-500' : 'opacity-40 cursor-not-allowed'}`}
                          title={isEditable ? "Supprimer" : "Activez Mode Lab"}><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                </div>

                {/* ═══════════════════════════════════ */}
                {/* VUE DESKTOP md+ : tableau 12 col    */}
                {/* ═══════════════════════════════════ */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">

                  {/* CHECKBOX */}
                  <div className="col-span-1 flex items-center justify-center">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelectCohorte(id)}
                      className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-emerald-500" />
                  </div>

                  {/* COHORTE */}
                  <div className="col-span-3">
                    {isEdited ? (
                      <div className="space-y-1">
                        <input type="text" value={current.nom || ''} onChange={e => updateEditedField(id, 'nom', e.target.value)}
                          placeholder="Nom" className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-emerald-500 rounded" />
                        <div className="text-[10px] text-zinc-500 font-mono">{current.slug}</div>
                      </div>
                    ) : (
                      <>
                        <div className="font-bold text-sm">{current.nom}</div>
                        <div className="text-[10px] text-zinc-500 font-mono">{current.slug}</div>
                        {current.description && (
                          <div className="text-[10px] text-zinc-600 mt-1 truncate max-w-xs">{current.description}</div>
                        )}
                      </>
                    )}
                  </div>

                  {/* PÉRIODE */}
                  <div className="col-span-2">
                    {isEdited ? (
                      <div className="space-y-1">
                        <input type="date" value={current.debut || ''} onChange={e => updateEditedField(id, 'debut', e.target.value)}
                          className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs outline-none focus:border-emerald-500 rounded" />
                        <input type="date" value={current.finPrevue || ''} onChange={e => updateEditedField(id, 'finPrevue', e.target.value)}
                          className={`w-full bg-zinc-900 border px-2 py-1 text-xs outline-none focus:border-emerald-500 rounded ${current.finPrevue?.invalid ? 'border-red-500' : 'border-zinc-700'}`} />
                        {current.finPrevue?.invalid && <span className="text-[9px] text-red-400">Date invalide</span>}
                      </div>
                    ) : (
                      <div className="text-zinc-400 font-mono text-[11px]">
                        <div>{formatDate(current.debut)}</div>
                        {current.finPrevue && <div className="text-zinc-600">→ {formatDate(current.finPrevue)}</div>}
                      </div>
                    )}
                  </div>

                  {/* RESPONSABLE */}
                  <div className="col-span-2">
                    {isEdited ? (
                      <select value={current.responsableId || ''} onChange={e => updateEditedField(id, 'responsableId', e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm outline-none focus:border-emerald-500 rounded">
                        <option value="">Sélectionner...</option>
                        {availableResponsables.map(r => (
                          <option key={r._id || r.id} value={r._id || r.id}>{r.nom} {r.prenom}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-sm text-zinc-300">
                        {responsable ? `${responsable.nom} ${responsable.prenom}` : '—'}
                      </div>
                    )}
                  </div>

                  {/* APPRENANTS */}
                  <div className="col-span-1 text-center">
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-950/30 text-emerald-400 font-bold text-sm rounded">
                      <Users size={12} />
                      {stats.nbEleves}
                    </div>
                  </div>

                  {/* PROGRESSION */}
                  <div className="col-span-1">
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-full bg-zinc-800 h-1.5 rounded overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 transition-all"
                          style={{ width: `${stats.progressionMoyenne || 0}%` }} />
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500">{stats.progressionMoyenne || 0}%</span>
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className="col-span-2 flex items-center justify-end gap-2 opacity-60 hover:opacity-100 transition-opacity">
                    {isEdited ? (
                      <>
                        <button onClick={() => saveCohorte(id)} className="p-1.5 hover:text-emerald-400 transition-colors" title="Sauvegarder"><Save size={14} /></button>
                        <button onClick={() => cancelEditCohorte(id)} className="p-1.5 hover:text-red-400 transition-colors" title="Annuler"><X size={14} /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setDetailModal(c)} className="p-1.5 hover:text-sky-400 transition-colors" title="Voir détails"><Eye size={14} /></button>
                        <button onClick={() => startEditCohorte(c)} disabled={!isEditable}
                          className={`p-1.5 transition-colors ${isEditable ? 'hover:text-emerald-400 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                          title={isEditable ? "Éditer cohorte" : "Activez Mode Lab"}><Edit size={14} /></button>
                        {responsable && (
                          <button onClick={() => openCompose({ id: responsable._id || responsable.id, name: `${responsable.nom} ${responsable.prenom}`, email: responsable.email })}
                            className="p-1.5 hover:text-sky-400 transition-colors" title="Contacter responsable"><Mail size={14} /></button>
                        )}
                        <button onClick={() => handleDuplicate(id)} className="p-1.5 hover:text-purple-400 transition-colors" title="Dupliquer"><Copy size={14} /></button>
                        <button onClick={() => { if (window.confirm('Supprimer cette cohorte ?')) deleteCohorte(id); }}
                          disabled={!isEditable}
                          className={`p-1.5 transition-colors ${isEditable ? 'hover:text-red-500 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                          title={isEditable ? "Supprimer cohorte" : "Activez Mode Lab"}><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                </div>

              </div>
            );
          })
        ) : (
          <div className="py-20 text-center text-zinc-600 font-mono text-xs uppercase tracking-widest">
            Aucune cohorte détectée
          </div>
        )}
      </div>
    </div>

    {/* MODAL DÉTAILS */}
    {detailModal && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
        <div className="w-full max-w-4xl max-h-[90vh] bg-zinc-900 border border-zinc-700 shadow-2xl rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-zinc-800 bg-zinc-950">
            <div>
              <h3 className="text-lg md:text-xl font-bold text-emerald-400">{detailModal.nom}</h3>
              <p className="text-sm text-zinc-500 font-mono">{detailModal.slug}</p>
            </div>
            <button onClick={() => setDetailModal(null)} className="p-2 hover:bg-zinc-800 rounded transition-colors">
              <X size={20} />
            </button>
          </div>

          <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Infos cohorte */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-emerald-400 uppercase tracking-wide border-b border-zinc-800 pb-2">
                  Informations Cohorte
                </h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <label className="text-[10px] uppercase text-zinc-500">Status</label>
                    <div className="mt-0.5">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-black border rounded uppercase ${
                        detailModal.status === 'active' ? 'bg-emerald-900/20 text-emerald-400 border-emerald-500/30'
                        : detailModal.status === 'planned' ? 'bg-sky-900/20 text-sky-400 border-sky-500/30'
                        : 'bg-zinc-800 text-zinc-500 border-zinc-700'
                      }`}>{detailModal.status}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-zinc-500">Période</label>
                    <div className="text-zinc-300">{formatDate(detailModal.debut)} → {formatDate(detailModal.finPrevue || detailModal.finReelle)}</div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-zinc-500">Responsable</label>
                    <div className="text-zinc-300">
                    {(() => {
                      const r = staff.find(s =>
                        String(s._id || s.id) === String(detailModal.responsableId?._id || detailModal.responsableId)
                      );
                      return r ? `${r.nom} ${r.prenom || ''}`.trim() : '—';
                    })()}                    </div>
                  </div>
                  {detailModal.description && (
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500">Description</label>
                      <div className="text-zinc-300">{detailModal.description}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-emerald-400 uppercase tracking-wide border-b border-zinc-800 pb-2">Statistiques</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-zinc-950 p-4 rounded border border-zinc-800">
                    <div className="text-[10px] uppercase text-zinc-500 mb-1">Apprenants</div>
                    <div className="text-2xl font-black text-emerald-400">
                      {(detailModal.stats || getCohorteStats(detailModal._id || detailModal.id)).nbEleves}
                    </div>
                  </div>
                  <div className="bg-zinc-950 p-4 rounded border border-zinc-800">
                    <div className="text-[10px] uppercase text-zinc-500 mb-1">Progression Moy</div>
                    <div className="text-2xl font-black text-emerald-400">
                      {(detailModal.stats || getCohorteStats(detailModal._id || detailModal.id)).progressionMoyenne}%
                    </div>
                  </div>
                  <div className="bg-zinc-950 p-4 rounded border border-zinc-800 col-span-2">
                    <div className="text-[10px] uppercase text-zinc-500 mb-1">Parcours assignés</div>
                    <div className="text-xl font-black text-sky-400">
                      {Array.isArray(detailModal.parcoursIds) ? detailModal.parcoursIds.length : 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Parcours */}
              <div className="md:col-span-2">
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wide border-b border-zinc-800 pb-2 mb-4">
                  Parcours (MasterParcours)
                </h4>
                <div className="space-y-2">
                  {Array.isArray(detailModal.parcoursIds) && detailModal.parcoursIds.length > 0 ? (
                    detailModal.parcoursIds.map((pId, idx) => {
                      const parcours = courses.find(c => (c._id || c.id) === (pId._id || pId));
                      return (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-zinc-950/50 border border-zinc-800 rounded">
                          <BookOpen size={16} className="text-sky-400 shrink-0" />
                          <div>
                            <div className="font-medium text-zinc-200">{parcours?.nom || 'Parcours inconnu'}</div>
                            {parcours?.slug && <div className="text-[10px] text-zinc-500 font-mono">{parcours.slug}</div>}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-zinc-500 text-sm">Aucun parcours assigné</div>
                  )}
                </div>
              </div>

              {/* Notes internes */}
              {detailModal.notesInternes && (
                <div className="md:col-span-2">
                  <h4 className="text-sm font-bold text-amber-400 uppercase tracking-wide border-b border-zinc-800 pb-2 mb-4">Notes Internes</h4>
                  <div className="bg-zinc-950 p-4 rounded border border-zinc-800 text-sm text-zinc-300 whitespace-pre-wrap">
                    {detailModal.notesInternes}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-zinc-800 flex flex-wrap justify-end gap-3">
              <button onClick={() => { startEditCohorte(detailModal); setDetailModal(null); }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded flex items-center gap-2">
                <Edit size={14} />Éditer
              </button>
              <button onClick={() => setDetailModal(null)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-sm rounded">
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

  </div>
);
}